import type { ReactNode } from "react";
import { Button, FieldHint, FieldLabel, Select } from "@amber/ui";
import type { ProfileOption } from "@amber/profiles/contracts";
import { CADENCE_TIERS, RELATIONSHIPS, RELATIONSHIP_LABELS, type Relationship } from "../../contracts/index";

export type Tier = (typeof CADENCE_TIERS)[number];

export interface PersonEntry {
  profile_id: ProfileOption["id"];
  relationship: Relationship | null;
  inPlan: boolean;
  required: boolean;
}

export function PersonCard({
  entry: p,
  name,
  planLabel = "Add to their plan",
  onUpdate,
  onRemove,
}: {
  entry: PersonEntry;
  name: string;
  planLabel?: string;
  onUpdate: (patch: Partial<PersonEntry>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="mb-2 rounded-lg border border-divider p-2">
      <div className="flex items-center gap-2">
        <b className="min-w-0 flex-1 truncate font-strong text-fg">{name}</b>
        <label className="sr-only" htmlFor={`rel-${p.profile_id}`}>
          How {name} relates to this source
        </label>
        <span className="w-[150px] flex-none">
          <Select
            id={`rel-${p.profile_id}`}
            className="h-7"
            value={p.relationship ?? ""}
            onChange={(e) => {
              onUpdate({ relationship: RELATIONSHIPS.find((r) => r === e.target.value) ?? null });
            }}
          >
            {p.relationship === null ? <option value="">No relationship</option> : null}
            {RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {RELATIONSHIP_LABELS[r]}
              </option>
            ))}
          </Select>
        </span>
        <Button size="sm" aria-label={`Remove ${name}`} onClick={onRemove}>
          Remove
        </Button>
      </div>
      <label className="mt-1.5 flex items-center gap-2 text-md">
        <input
          type="checkbox"
          className="accent-[var(--color-link)]"
          checked={p.inPlan}
          onChange={(e) => {
            onUpdate({ inPlan: e.target.checked, required: e.target.checked && p.required });
          }}
        />
        {planLabel}
      </label>
      {p.inPlan ? (
        <label className="mt-1 ml-5 flex items-center gap-2 text-md">
          <input
            type="checkbox"
            className="accent-[var(--color-link)]"
            checked={p.required}
            onChange={(e) => {
              onUpdate({ required: e.target.checked });
            }}
          />
          Required route
        </label>
      ) : null}
    </div>
  );
}

export function PersonOptions({
  options,
  onPick,
}: {
  options: readonly ProfileOption[];
  onPick: (id: ProfileOption["id"]) => void;
}) {
  if (options.length === 0) return null;
  return (
    <ul className="m-0 mt-1 max-h-[120px] list-none overflow-auto rounded-lg border border-divider p-1">
      {options.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            className="flex h-[27px] w-full items-center rounded-md px-2 text-left hover:bg-surface-2"
            onClick={() => {
              onPick(p.id);
            }}
          >
            {p.name}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function personOptions(
  profiles: readonly ProfileOption[] | undefined,
  find: string,
  chosen: readonly { profile_id: string }[],
): ProfileOption[] {
  const term = find.trim().toLowerCase();
  return (profiles ?? []).filter(
    (p) =>
      p.lifecycle_state !== "archived" &&
      term !== "" &&
      p.name.toLowerCase().includes(term) &&
      !chosen.some((x) => x.profile_id === p.id),
  );
}

export function TierField({
  id,
  value,
  onChange,
  preview,
}: {
  id: string;
  value: Tier;
  onChange: (tier: Tier) => void;
  preview?: ReactNode;
}) {
  return (
    <div className="mt-3">
      <FieldLabel htmlFor={id}>Cadence tier</FieldLabel>
      <Select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(CADENCE_TIERS.find((t) => t === e.target.value) ?? "standard");
        }}
      >
        <option value="priority">Priority · every 2 h; found within 4 h</option>
        <option value="standard">Standard · every 12 h; found within 24 h</option>
        <option value="low">Low · every 24 h; found within 48 h</option>
      </Select>
      {preview}
      <FieldHint>
        Required routes default to Priority. A YouTube channel uses about 3 quota units per scan; listing reads cost
        nothing.
      </FieldHint>
    </div>
  );
}
